/**
 * Types liés au système de Défis Sociaux (challenge_posts / challenge_answers / parental_consents).
 */

/** `stream` : l'élève s'est filmé pendant tout l'exercice (défi streamé, 31/10/2026). */
export type ShareType = 'start' | 'step' | 'final' | 'stream';
export type ChallengeMediaType = 'none' | 'photo' | 'video';

export interface ChallengePost {
  id: string;
  userId: string;
  exerciseId: string;
  attemptId: string | null;
  stepId: string | null;
  blockId: string | null;
  shareType: ShareType;
  title: string;
  mediaType: ChallengeMediaType;
  mediaUrl: string | null;
  thumbnailUrl: string | null;
  transcription: string | null;
  isPublic: boolean;
  isFlagged: boolean;
  isModerated: boolean;
  challengeSlug: string;
  totalViews: number;
  totalAttempts: number;
  totalSuccess: number;
  createdAt: string;
}

export interface ChallengeAnswer {
  id: string;
  challengePostId: string;
  userId: string | null;
  selectedChoiceId: string;
  isCorrect: boolean;
  answeredAt: string;
}

export interface ParentalConsent {
  id: string;
  userId: string;
  isMinor: boolean;
  parentEmail: string | null;
  canSharePublicly: boolean;
  canUseSelfie: boolean;
  canUseVideo: boolean;
  canBeRepostedByJeraisonne: boolean;
  useAnonymousPseudo: boolean;
  acceptedAt: string | null;
  revokedAt: string | null;
  ipAddress: string | null;
  createdAt: string;
  updatedAt: string;
}

/** Contexte SolvePath capturé au moment du partage */
export interface ChallengeSolveContext {
  shareType: ShareType;
  exerciseId: string;
  attemptId: string | null;
  /** ID de l'étape en cours (null si share_type = 'start') */
  stepId: string | null;
  /** Index de l'étape dans le SolvePath (null si 'start') */
  stepIndex: number | null;
}

/** Payload envoyé à l'edge function create-challenge */
export interface CreateChallengePayload {
  exerciseId: string;
  attemptId: string | null;
  /** V1 : id d'un exercise_steps (exclusif avec blockId) */
  stepId: string | null;
  /** V2 : id du bloc statement_question partagé (exclusif avec stepId) */
  blockId: string | null;
  shareType: ShareType;
  title: string;
  mediaType: ChallengeMediaType;
  transcription?: string | null;
  /**
   * L'auteur demande à montrer son défi sur la page d'accueil (migration 0726) :
   * il reste privé (ouvert par son lien) tant qu'un admin ne l'a pas validé.
   */
  requestPublic?: boolean;
  /** Défi streamé : la chronologie de l'exercice (`StreamEvent[]`, `challenge-stream.ts`). */
  streamTimeline?: unknown[];
}

/** Réponse de l'edge function create-challenge */
export interface CreateChallengeResponse {
  slug: string;
  challengeUrl: string;
}

/**
 * Résout, au moment du partage, la référence d'étape à stocker selon le modèle
 * de l'exercice : V2 → blockId (exercise_blocks), V1 → stepId (exercise_steps).
 * Seul `share_type='step'` porte une référence ; start/final → aucune.
 *
 * Garantit l'exclusivité step/block (cf. contrainte SQL challenge_posts_step_xor_block,
 * migration 0105) : on ne renvoie jamais les deux non-null.
 */
export function resolveSharedStepRef(params: {
  isV2: boolean;
  shareType: ShareType;
  currentStepId: string | null;
}): { stepId: string | null; blockId: string | null } {
  const ref = params.shareType === 'step' ? params.currentStepId : null;
  if (!ref) return { stepId: null, blockId: null };
  return params.isV2 ? { stepId: null, blockId: ref } : { stepId: ref, blockId: null };
}
